/**
 * ════════════════════════════════════════════════════════════════
 *  BRAND CONSTANTS — Single source of truth for Decorum Lifestyle™
 *  Use these everywhere. Never hardcode the brand name in chrome,
 *  page titles, OG tags, or marketing components.
 * ════════════════════════════════════════════════════════════════
 *
 *  Plain string variants (for <title>, alt, meta, JSON-LD, etc.):
 *    - BRAND.name        → "Decorum Lifestyle™"
 *    - BRAND.nameNoMark  → "Decorum Lifestyle"   (use only in body prose)
 *    - BRAND.legalName   → "Decorum Lifestyle™"
 *
 *  React-safe variants (renders ™ as a styled <sup>):
 *    - <BrandName />            → inline brand mark for headings/UI
 *    - <BrandName withMark />   → same (default)
 *    - <BrandName withMark={false} /> → no ™
 *
 *  SEO helper:
 *    - usePageMeta({ title, description }) keeps <title> + meta in
 *      sync on route changes without an external lib.
 */

import { useEffect } from "react";

export const BRAND = {
  name: "Decorum Lifestyle™",
  nameNoMark: "Decorum Lifestyle",
  legalName: "Decorum Lifestyle™",
  tagline: "Honor · Discipline · Obedience",
  shortTagline: "Theology · Psychology · Application",
  description:
    "Decorum Lifestyle™ — a 9-level developmental framework integrating Kingdom theology, psychology, and elite discipline. Honor. Discipline. Obedience.",
  shortDescription:
    "Executive Coaching · Kingdom Formation · Elite Discipline",
  domain: "decorumlifestyle.com",
  url: "https://decorumlifestyle.com",
  ogImage: "/images/decorum-logo.jpg",
  // Default page-title suffix: `<page> | Decorum Lifestyle™`
  titleSuffix: "Decorum Lifestyle™",
} as const;

/** Build a consistent <title> string: "Page Name | Decorum Lifestyle™" */
export function buildPageTitle(pageName?: string): string {
  if (!pageName) return `${BRAND.name} | ${BRAND.tagline}`;
  return `${pageName} | ${BRAND.titleSuffix}`;
}

/**
 * Lightweight client-side meta sync. Drop into any route component:
 *   usePageMeta({ title: "Dashboard", description: "..." });
 * Updates <title>, meta description, og:title, og:description, twitter equivalents.
 * Restores previous values on unmount so back-nav looks clean.
 */
export function usePageMeta(opts: {
  title?: string;
  description?: string;
  path?: string;
}) {
  useEffect(() => {
    const previousTitle = document.title;
    const nextTitle = buildPageTitle(opts.title);
    document.title = nextTitle;

    const canonicalPath =
      opts.path ??
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = `${BRAND.url}${
      canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`
    }`;

    const updates: Array<[string, string, string | undefined]> = [
      ["name", "description", opts.description],
      ["property", "og:title", nextTitle],
      ["property", "og:description", opts.description],
      ["property", "og:url", canonicalUrl],
      ["name", "twitter:title", nextTitle],
      ["name", "twitter:description", opts.description],
    ];

    const restores: Array<() => void> = [];
    for (const [attr, key, value] of updates) {
      if (!value) continue;
      let el = document.head.querySelector<HTMLMetaElement>(
        `meta[${attr}="${key}"]`
      );
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", value);
      restores.push(() => {
        if (created) el!.remove();
        else if (prev !== null) el!.setAttribute("content", prev);
      });
    }

    // Canonical <link> — update or create, then restore on unmount
    let linkEl = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const linkCreated = !linkEl;
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.setAttribute("rel", "canonical");
      document.head.appendChild(linkEl);
    }
    const prevHref = linkEl.getAttribute("href");
    linkEl.setAttribute("href", canonicalUrl);

    return () => {
      document.title = previousTitle;
      restores.forEach((fn) => fn());
      if (linkCreated) linkEl?.remove();
      else if (prevHref !== null) linkEl?.setAttribute("href", prevHref);
    };
  }, [opts.title, opts.description, opts.path]);
}
